import api, { tokenManager } from './api';
import { LoginCredentials, Member, AuthTokens } from '../types/auth.types';
import { deviceTokenManager } from '../utils/deviceToken';

interface LoginResponse {
  success: boolean;
  message: string;
  data: {
    member?: Member;
    access_token?: string;
    refresh_token?: string;
    expires_in?: number;
    two_fa_required?: boolean;
    user_id?: number;
    method?: 'email' | 'sms';
  };
}

interface TwoFactorSendResponse {
  success: boolean;
  message: string;
  data: {
    two_fa_required?: boolean;
    user_id?: number;
    method?: 'email' | 'sms';
    expires_in?: number;
    // Fields returned when device is trusted (no 2FA needed)
    member?: Member;
    access_token?: string;
    refresh_token?: string;
    device_token?: string;
  };
}

interface LogoutResponse {
  success: boolean;
  message: string;
}

const MEMBER_KEY = 'member_data';

class AuthService {
  // Store member data in localStorage
  private storeMember(member: Member): void {
    localStorage.setItem(MEMBER_KEY, JSON.stringify(member));
  }

  // Retrieve member data from localStorage
  getMember(): Member | null {
    const memberData = localStorage.getItem(MEMBER_KEY);
    // Handle cases where localStorage contains "undefined" string
    if (!memberData || memberData === 'undefined' || memberData === 'null') {
      return null;
    }
    try {
      return JSON.parse(memberData);
    } catch (e) {
      console.error('Failed to parse member data:', e);
      // Clear invalid data
      localStorage.removeItem(MEMBER_KEY);
      return null;
    }
  }

  // Clear member data from localStorage
  private clearMember(): void {
    localStorage.removeItem(MEMBER_KEY);
  }
  async sendTwoFactorCode(email: string, password: string, method: 'email' | 'sms' = 'email'): Promise<TwoFactorSendResponse> {
    try {
      // Get device token if exists (from cookie or localStorage)
      const deviceToken = deviceTokenManager.getToken();
      
      const response = await api.post<TwoFactorSendResponse>('/auth/two-factor', {
        email,
        password,
        method,
        device_token: deviceToken
      }, {
        params: { action: 'send' }
      });
      
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to send verification code');
    }
  }

  async login(credentials: LoginCredentials): Promise<{ member?: Member; tokens?: AuthTokens; twoFactorRequired?: boolean; userId?: number; method?: string }> {
    try {
      // Get device token if exists (from cookie or localStorage)
      const deviceToken = deviceTokenManager.getToken();
      console.log('[AuthService] Device token found:', deviceToken ? 'Yes' : 'No');
      
      // Add device token to credentials if it exists
      const loginData = {
        ...credentials,
        ...(deviceToken && { device_token: deviceToken })
      };
      console.log('[AuthService] Login data being sent:', { ...loginData, password: '[REDACTED]' });
      
      // First attempt with the old login endpoint for backward compatibility
      const response = await api.post<LoginResponse>('/auth/login', loginData);
      
      console.log('[AuthService] Login response:', response.data);
      
      // Handle both success:true and direct data response formats
      const responseData: any = response.data.success ? response.data.data : response.data;
      
      if (responseData) {
        const { member, access_token, refresh_token, expires_in, two_fa_required, user_id, method } = responseData;
        
        // Check if 2FA is required
        if (two_fa_required) {
          return {
            twoFactorRequired: true,
            userId: user_id,
            method: method
          };
        }
        
        // No 2FA required, proceed with normal login
        if (member && access_token) {
          tokenManager.setTokens(access_token, refresh_token);
          this.storeMember(member);
          
          return {
            member: member,
            tokens: {
              access_token,
              refresh_token,
              expires_in: expires_in || 3600,
            },
          };
        }
      }
      
      throw new Error(response.data.message || 'Login failed');
    } catch (error: any) {
      // If old login endpoint fails with 404, try the new 2FA flow
      if (error.response?.status === 404 || error.response?.data?.two_fa_required) {
        try {
          const twoFactorResponse = await this.sendTwoFactorCode(
            credentials.email,
            credentials.password
          );
          
          if (twoFactorResponse.data.two_fa_required) {
            return {
              twoFactorRequired: true,
              userId: twoFactorResponse.data.user_id,
              method: twoFactorResponse.data.method
            };
          }
        } catch (twoFactorError: any) {
          throw new Error(twoFactorError.message || 'Login failed');
        }
      }
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }

  async logout(): Promise<void> {
    try {
      await api.post<LogoutResponse>('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      tokenManager.clearTokens();
      this.clearMember();
      // DO NOT clear device trust token on logout - it should persist for 30 days
      // deviceTokenManager.clearToken(); // REMOVED - device trust should survive logout
      // Clear any other cached data
      localStorage.removeItem('selectedCustomer');
      localStorage.removeItem('voicemail_last_refresh');
      // Clear all session data including loginAsCustomer
      sessionStorage.clear();
    }
  }

  async refreshToken(): Promise<{ member: Member; tokens: AuthTokens }> {
    const refreshToken = tokenManager.getRefreshToken();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await api.post<LoginResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      });
      
      if (response.data.success && response.data.data.member && response.data.data.access_token) {
        const { member, access_token, expires_in } = response.data.data;
        
        // Update access token and member data
        tokenManager.setTokens(access_token);
        this.storeMember(member);
        
        return {
          member: member,
          tokens: {
            access_token,
            expires_in: expires_in || 3600,
          },
        };
      }
      
      throw new Error(response.data.message || 'Token refresh failed');
    } catch (error: any) {
      // Only clear tokens if we get a 401 (unauthorized) response
      // Don't clear for network errors or other temporary issues
      if (error.response?.status === 401) {
        tokenManager.clearTokens();
        this.clearMember();
      }
      throw new Error(error.response?.data?.message || 'Token refresh failed');
    }
  }

  isAuthenticated(): boolean {
    return !!tokenManager.getAccessToken();
  }

  // Force logout on auth failure - used by interceptors
  forceLogout(): void {
    tokenManager.clearTokens();
    this.clearMember();
    localStorage.removeItem('selectedCustomer');
    localStorage.removeItem('voicemail_last_refresh');
    sessionStorage.clear();
    window.location.href = '/login';
  }

  // Revoke device trust - call this when user wants to untrust the device
  revokeDeviceTrust(): void {
    deviceTokenManager.clearToken();
    console.log('[AuthService] Device trust revoked');
  }
}

const authService = new AuthService();
export default authService;